import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Field } from '../patterns';
import {
  nutritionService,
  receiveStock,
} from '../../services/nutritionService';
import type { FertilizerProduct } from '../../services/nutritionService';

export interface ReceiveStockModalProps {
  show: boolean;
  onHide: () => void;
  onReceived: () => void;
}

const today = () => new Date().toISOString().slice(0, 10);

const ReceiveStockModal: React.FC<ReceiveStockModalProps> = ({ show, onHide, onReceived }) => {
  const { t } = useTranslation();
  const [catalog, setCatalog] = useState<FertilizerProduct[]>([]);
  const [productId, setProductId] = useState('');
  const [qty, setQty] = useState('');
  const [unit, setUnit] = useState('');
  const [unitPrice, setUnitPrice] = useState('');
  const [supplier, setSupplier] = useState('');
  const [location, setLocation] = useState('');
  const [receivedAt, setReceivedAt] = useState(today());
  const [dwellLimitDays, setDwellLimitDays] = useState('30');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!show) return;
    setProductId('');
    setQty('');
    setUnit('');
    setUnitPrice('');
    setSupplier('');
    setLocation('');
    setReceivedAt(today());
    setDwellLimitDays('30');
    setSubmitting(false);
    setError(null);
    nutritionService.listFertilizers().then(setCatalog).catch(() => {});
  }, [show]);

  const selectedProduct = catalog.find((p) => p._id === productId);

  useEffect(() => {
    if (selectedProduct?.stockUnit) setUnit(selectedProduct.stockUnit);
  }, [selectedProduct]);

  const canSubmit = productId && qty && Number(qty) > 0 && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      await receiveStock({
        productId,
        productName: selectedProduct?.productName,
        qty: Number(qty),
        unit: unit || undefined,
        unitPrice: unitPrice ? Number(unitPrice) : undefined,
        supplier: supplier || undefined,
        location: location || undefined,
        receivedAt,
        dwellLimitDays: Number(dwellLimitDays) || 30,
      });
      onReceived();
      onHide();
    } catch {
      setError(t('nutrition.inventory.receiveModal.error'));
      setSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} title={t('nutrition.inventory.receiveModal.title')}>
      <Modal.Body>
        {error && (
          <div className="ntr-error" role="alert">{error}</div>
        )}

        <Field label={t('nutrition.inventory.receiveModal.product')} required controlId="rs-product">
          <select
            id="rs-product"
            className="pat-input"
            value={productId}
            onChange={(e) => setProductId(e.target.value)}
          >
            <option value="">—</option>
            {catalog.map((p) => (
              <option key={p._id} value={p._id}>{p.productName}</option>
            ))}
          </select>
        </Field>

        <Field label={t('nutrition.inventory.receiveModal.qty')} required controlId="rs-qty">
          <input
            id="rs-qty"
            type="number"
            min="0"
            step="any"
            className="pat-input"
            value={qty}
            onChange={(e) => setQty(e.target.value)}
          />
        </Field>

        <Field label={t('nutrition.inventory.receiveModal.unit')} controlId="rs-unit">
          <input
            id="rs-unit"
            type="text"
            className="pat-input"
            value={unit}
            onChange={(e) => setUnit(e.target.value)}
          />
        </Field>

        <Field label={t('nutrition.inventory.receiveModal.unitPrice')} controlId="rs-unitPrice">
          <input
            id="rs-unitPrice"
            type="number"
            min="0"
            step="0.01"
            className="pat-input"
            value={unitPrice}
            onChange={(e) => setUnitPrice(e.target.value)}
          />
        </Field>

        <Field label={t('nutrition.inventory.receiveModal.supplier')} controlId="rs-supplier">
          <input
            id="rs-supplier"
            type="text"
            className="pat-input"
            value={supplier}
            onChange={(e) => setSupplier(e.target.value)}
          />
        </Field>

        <Field label={t('nutrition.inventory.receiveModal.location')} controlId="rs-location">
          <input
            id="rs-location"
            type="text"
            className="pat-input"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
          />
        </Field>

        <Field label={t('nutrition.inventory.receiveModal.receivedAt')} required controlId="rs-receivedAt">
          <input
            id="rs-receivedAt"
            type="date"
            className="pat-input"
            value={receivedAt}
            onChange={(e) => setReceivedAt(e.target.value)}
          />
        </Field>

        <Field label={t('nutrition.inventory.receiveModal.dwellLimitDays')} controlId="rs-dwell">
          <input
            id="rs-dwell"
            type="number"
            min="1"
            step="1"
            className="pat-input"
            value={dwellLimitDays}
            onChange={(e) => setDwellLimitDays(e.target.value)}
          />
        </Field>
      </Modal.Body>
      <Modal.Footer>
        <button
          type="button"
          className="pat-btn pat-btn--subtle"
          onClick={onHide}
          disabled={submitting}
        >
          {t('nutrition.inventory.receiveModal.cancel')}
        </button>
        <button
          type="button"
          className="pat-btn pat-btn--primary"
          onClick={handleSubmit}
          disabled={!canSubmit}
        >
          {t('nutrition.inventory.receiveModal.submit')}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default ReceiveStockModal;
